/**
 * The Result type used by every fallible operation in the coverage engine.
 *
 * WHY THIS IS NOT `@nexus/types/server`'s `Result`. That file is pure and would
 * be reusable, but this package's ESLint config refuses any import matching
 * `**\/server/*` from frontend-safe code — the rule exists because `src/server`
 * re-exports the 27 MB Prisma client and one import puts it back into the
 * frontend's TypeScript program. The coverage engine runs in the browser (the
 * authoring UI computes the number the server stores), so it lives on the
 * frontend-safe side and cannot reach that file. The shape below is
 * deliberately IDENTICAL (`ok` / `value` / `error`) so the two interoperate by
 * structure at any seam that needs both.
 *
 * A failure here is always a VALUE, never a thrown exception. Nothing in this
 * module throws during evaluation: a customer-facing ROI figure that fails must
 * degrade into a named warning, and an exception crossing a render boundary
 * cannot do that.
 */

/** A successful computation carrying its value. */
export interface CoverageOk<T> {
  readonly ok: true;
  readonly value: T;
}

/** A failed computation carrying a typed, inspectable reason. */
export interface CoverageErr<E> {
  readonly ok: false;
  readonly error: E;
}

/** Either a value or a typed failure. Never a thrown exception, never `NaN`. */
export type CoverageResult<T, E> = CoverageOk<T> | CoverageErr<E>;

/** Wrap a value as a success. */
export function coverageOk<T>(value: T): CoverageOk<T> {
  return { ok: true, value };
}

/** Wrap a typed reason as a failure. */
export function coverageErr<E>(error: E): CoverageErr<E> {
  return { ok: false, error };
}

/** Narrow a result to its success arm. */
export function isCoverageOk<T, E>(result: CoverageResult<T, E>): result is CoverageOk<T> {
  return result.ok;
}

/** Narrow a result to its failure arm. */
export function isCoverageErr<T, E>(result: CoverageResult<T, E>): result is CoverageErr<E> {
  return !result.ok;
}
